import { useEffect } from 'react'

import { z } from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormDescription,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  NumberInput,
  SearchSelect,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { POSITION_COMPONENT_TYPES } from '@/pages/pnl-attributions/constants'
import { usePnlContext } from '@/pages/pnl-attributions/context'
import { useAddToTradeMutation } from '@/pages/pnl-attributions/mutations'

export interface AddToTradeDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    trade_id: string
    attribution_id: number
    batch_id?: number | null
  }
}

const addToTradeSchema = z.object({
  component_type: z.enum(Object.values(POSITION_COMPONENT_TYPES)),
  amount_base: z.coerce.number(),
  reason: z.string().min(1, 'You must provide a reason for adding a component.'),
  source_reference: z.string().optional().nullable(),
})

export const AddToTradeDialog: React.FC<AddToTradeDialogProps> = ({ mutationParams, ...props }) => {
  const { params } = usePnlContext()

  const addToTradeMutation = useAddToTradeMutation({
    fund_name: params.fund_name,
    value_date: params.value_date,
  })

  const form = useFormWithSchema({ mode: 'all' }, addToTradeSchema)

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await addToTradeMutation.mutateAsync({
      ...data,
      attribution_id: mutationParams.attribution_id,
      batch_id: mutationParams.batch_id,
    })
  })
  return (
    <ConfirmationDialog
      {...props}
      action={onSubmit}
      title={`Add PnL component to trade ${mutationParams.trade_id}`}
      description={`This will add a new P&L component to this trade. `}
      valid={form.formState.isValid}
      pending={addToTradeMutation.isPending}
    >
      <Form {...form}>
        <div className="space-y-4">
          <FormFieldRow>
            <FormField
              name="component_type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Component Type</FormLabel>
                    <FormError />
                  </div>
                  <SearchSelect
                    value={field.value}
                    setValue={(val) => field.onChange(val)}
                    placeholder="Select a component type"
                    options={Object.entries(POSITION_COMPONENT_TYPES).map(([key, value]) => {
                      return { label: key, value }
                    })}
                  />
                </FormItem>
              )}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              name="amount_base"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Amount Base</FormLabel>
                    <FormError />
                  </div>
                  <NumberInput
                    thousandSeparator={','}
                    value={field.value}
                    onValueChange={(values) => field.onChange(values.floatValue)}
                  />
                </FormItem>
              )}
            />
          </FormFieldRow>

          <FormFieldRow>
            <FormField
              name="reason"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Reason</FormLabel>
                    <FormError />
                  </div>
                  <FormDescription>Provide a reason for adding this component.</FormDescription>
                  <Textarea value={field.value} onChange={(value) => field.onChange(value)} />
                </FormItem>
              )}
            />
          </FormFieldRow>
          <FormFieldRow>
            <FormField
              name="source_reference"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Source Reference</FormLabel>
                    <FormError />
                  </div>
                  <FormDescription>
                    Enter any information used as a reference for adding this component.
                  </FormDescription>
                  <Textarea value={field.value ?? ''} onChange={(value) => field.onChange(value)} />
                </FormItem>
              )}
            />
          </FormFieldRow>
        </div>
      </Form>
    </ConfirmationDialog>
  )
}
